import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useAdvancedFilterContext } from '../../context';
import FilterButton from '../../FilterButton';
import { AdvancedFilterListOption, AdvancedFilterProperties, availableProperties, ListProperties, NotBooleanProperties } from '../../types';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { ORDER_STATUS } from 'src/constants/order';
import { localeStore } from 'src/mobx/localesStore';
import { OrderDeliveryType, OrderingChannelType, OrderPaymentType } from 'src/mobx/ordersHistoryStore/types';
import Icon from 'src/view/components/Controls/Icon/Icon';
import SearchInput from 'src/view/components/Controls/SearchInput/SearchInput';
import OrderDeliveryTypeIcon from 'src/view/components/OrderDeliveryTypeIcon';
import OrderingChannelIcon from 'src/view/components/OrderingChannelIcon';
import OrderPaymentTypeIcon from 'src/view/components/OrderPaymentTypeIcon';
import OrderStatusIcon from 'src/view/components/OrderStatusIcon';
import './styles.scss';

interface ListFilterModalProps {
  onClose: () => void;
  property: ListProperties;
}

const ListIcon = ({ property, value }: { property: AdvancedFilterProperties; value: string }) => {
  switch (property) {
    case AdvancedFilterProperties.OrderType:
      return (
        <div className="px-2">
          <OrderDeliveryTypeIcon deliveryType={value as OrderDeliveryType} />
        </div>
      );
    case AdvancedFilterProperties.PaymentMethod:
      return (
        <div className="px-2">
          <OrderPaymentTypeIcon paymentType={value as OrderPaymentType} />
        </div>
      );
    case AdvancedFilterProperties.OrderingChannel:
      return (
        <div className="px-2">
          <OrderingChannelIcon orderingChannel={value as OrderingChannelType} />
        </div>
      );
    case AdvancedFilterProperties.Status:
      return (
        <div className="px-2">
          <OrderStatusIcon status={value as ORDER_STATUS} />
        </div>
      );
    default:
      return null;
  }
};

const ListFilterModal = ({ onClose, property }: ListFilterModalProps) => {
  const { store } = useAdvancedFilterContext();
  const [search, setSearch] = useState('');

  const handleClickOption = (option: AdvancedFilterListOption) => {
    store.selectOption(property, option);
  };

  const handleClearValues = useCallback(() => {
    store.resetPropertyValues(property as NotBooleanProperties);
  }, [property, store]);

  const handleSelectAllValues = useCallback(() => {
    store.selectAllOptions(property);
  }, [property, store]);

  useEffect(() => {
    if (property === AdvancedFilterProperties.Branch) {
      store.fetchBranchOptions();
    }
  }, []);

  return (
    <div>
      <div className="px-8 pt-5">
        <h3 className="family-bold color-dark text-size-26">{localeStore.t(availableProperties[property].title)}</h3>
      </div>
      <div className="close-icon" onClick={onClose}>
        <Icon name={Icons.Close} size={10} />
      </div>
      <div
        className={classNames('d-flex flex-1 px-8 py-4 flex-column advanced-filter-list-modal', {
          'min-height-400': property === AdvancedFilterProperties.Branch,
        })}
      >
        {property === AdvancedFilterProperties.Branch && (
          <SearchInput
            withResetIcon
            customPlaceholder
            onChangeText={setSearch}
            value={search}
            placeholder="txt_search"
            customPlaceholderIcon={Icons.Search}
            inputClassName="advanced-filter_search-input"
            customPlaceholderTextClassName="custom-picker__search-text"
            loading={store.options[property].length === 0}
          />
        )}
        <div className="options">
          {store.options[property]
            .filter((option) => !search || option.label.toLowerCase().includes(search.toLowerCase()))
            .map((option, i) => (
              <div key={i} className="list-option" onClick={() => handleClickOption(option)}>
                <ListIcon property={property} value={option.value} />
                <p className="option-text">{localeStore.t(option.label)}</p>
                {store.selectedOptions[property].has(option.value) && <Icon name={Icons.Check} size={24} className="color-mainblue" />}
              </div>
            ))}
        </div>
      </div>
      <div className="advanced-filter-buttons">
        <FilterButton title="apply" onClick={onClose} variant="primary" />
        <FilterButton title="select_all" onClick={handleSelectAllValues} />
        <FilterButton title="txt_clear" onClick={handleClearValues} />
      </div>
    </div>
  );
};

export default observer(ListFilterModal);
